import React, { useState, useEffect } from 'react';
import { getData, getUserProfile } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend } from 'recharts';

const BRAND = '#0f6e56';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = ['#0f6e56', '#185fa5', '#854f0b', '#a32d2d', '#534ab7', '#1d9e75', '#993556', '#5dcaa5'];

export default function Dashboard({ user, setPage }) {
  const [stats, setStats] = useState({ income: 0, expenses: 0, profit: 0, unpaid: 0, payable: 0 });
  const [monthly, setMonthly] = useState([]);
  const [expPie, setExpPie] = useState([]);
  const [recent, setRecent] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const load = async () => {
      const [invoices, expenses, prof] = await Promise.all([
        getData(user.uid, 'invoices'), getData(user.uid, 'expenses'), getUserProfile(user.uid)
      ]);
      setProfile(prof);
      const income = invoices.filter(i => i.type === 'sales' && i.status === 'paid').reduce((s, i) => s + i.amount, 0);
      const unpaid = invoices.filter(i => i.type === 'sales' && i.status === 'unpaid').reduce((s, i) => s + i.amount, 0);
      const payable = invoices.filter(i => i.type === 'purchase' && i.status === 'unpaid').reduce((s, i) => s + i.amount, 0);
      const totalExp = expenses.reduce((s, e) => s + e.amount, 0);
      setStats({ income, expenses: totalExp, profit: income - totalExp, unpaid, payable });

      setMonthly(MONTHS.map((m, i) => ({
        name: m,
        Income: invoices.filter(inv => inv.type === 'sales' && inv.status === 'paid' && new Date(inv.date).getMonth() === i).reduce((s, inv) => s + inv.amount, 0),
        Expenses: expenses.filter(e => new Date(e.date).getMonth() === i).reduce((s, e) => s + e.amount, 0),
      })));

      const byCat = {};
      expenses.forEach(e => { byCat[e.category] = (byCat[e.category] || 0) + e.amount; });
      setExpPie(Object.entries(byCat).map(([name, value]) => ({ name, value })));

      const txns = [
        ...invoices.slice(0, 6).map(i => ({ ...i, _t: 'invoice' })),
        ...expenses.slice(0, 6).map(e => ({ ...e, _t: 'expense' }))
      ].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 6);
      setRecent(txns);
    };
    load();
  }, [user.uid]);

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div style={{ background: `linear-gradient(135deg, #0a3d2e, #0f6e56 60%, #1d9e75)`, borderRadius: 16, padding: 28, marginBottom: 24, color: '#fff' }}>
        <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>Welcome back 👋</div>
        <div style={{ fontSize: 26, fontWeight: 700 }}>{profile?.businessName || user.email}</div>
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4, letterSpacing: 0.5 }}>Track. Manage. Grow.</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Income" value={fmt(stats.income)} color={BRAND} bg="#e1f5ee" icon="↑" onClick={() => setPage('reports')} />
        <StatCard label="Total Expenses" value={fmt(stats.expenses)} color="#a32d2d" bg="#fcebeb" icon="↓" onClick={() => setPage('expenses')} />
        <StatCard label="Net Profit" value={fmt(stats.profit)} color="#185fa5" bg="#e6f1fb" icon="📈" onClick={() => setPage('profitloss')} />
        <StatCard label="Receivable" value={fmt(stats.unpaid)} color="#854f0b" bg="#faeeda" icon="⏱" onClick={() => setPage('receivables')} />
        <StatCard label="Payable" value={fmt(stats.payable)} color="#993556" bg="#fbeaf0" icon="📤" onClick={() => setPage('payables')} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={card}>
          <div style={cardTitle}>Income vs Expenses — Monthly</div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthly}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={BRAND} stopOpacity={0.3} /><stop offset="95%" stopColor={BRAND} stopOpacity={0} /></linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#e24b4a" stopOpacity={0.3} /><stop offset="95%" stopColor="#e24b4a" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => fmt(v)} />
              <Legend />
              <Area type="monotone" dataKey="Income" stroke={BRAND} strokeWidth={2} fill="url(#gInc)" />
              <Area type="monotone" dataKey="Expenses" stroke="#e24b4a" strokeWidth={2} fill="url(#gExp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <div style={cardTitle}>Expenses by Category</div>
          {expPie.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa', fontSize: 14 }}>No expenses yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={expPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {expPie.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={card}>
          <div style={cardTitle}>Cash Position</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[
              { name: 'Income', value: stats.income, fill: BRAND },
              { name: 'Expenses', value: stats.expenses, fill: '#e24b4a' },
              { name: 'Receivable', value: stats.unpaid, fill: '#854f0b' },
              { name: 'Payable', value: stats.payable, fill: '#993556' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => fmt(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {[BRAND, '#e24b4a', '#854f0b', '#993556'].map((c, i) => <Cell key={i} fill={c} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <div style={cardTitle}>Recent Activity</div>
          {recent.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 0', color: '#aaa', fontSize: 14 }}>No transactions yet</div>
          ) : recent.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < recent.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: t._t === 'invoice' ? '#e1f5ee' : '#fcebeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{t._t === 'invoice' ? '📄' : '🧾'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{t._t === 'invoice' ? (t.customerName || t.supplierName || 'Invoice') : (t.description || 'Expense')}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{t.date}</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t._t === 'invoice' ? BRAND : '#a32d2d' }}>{t._t === 'invoice' ? '+' : '-'}{fmt(t.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, bg, icon, onClick }) {
  return (
    <div onClick={onClick} style={{ background: bg, borderRadius: 14, padding: 20, cursor: 'pointer', transition: 'transform 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
      <div style={{ fontSize: 20, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 13, color: '#555' }}>{label}</div>
    </div>
  );
}

const card = { background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e8e8e8' };
const cardTitle = { fontSize: 15, fontWeight: 600, color: '#222', marginBottom: 16 };
